import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { StatusBadge, PriorityBadge, LabelBadge } from "../components/badges";
import { CheckSquare, AlertCircle, CircleDot, CheckCircle2 } from "lucide-react";

const today = new Date().toISOString().slice(0, 10);

export default function MyWork() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ tasks: [], issues: [] });
  const [showDone, setShowDone] = useState(false);

  useEffect(() => {
    api.get("/my-work").then((r) => setData(r.data));
  }, []);

  const openTasks = data.tasks.filter((t) => t.status !== "completed");
  const doneTasks = data.tasks.filter((t) => t.status === "completed");
  const openIssues = data.issues.filter((i) => i.status === "open");
  const closedIssues = data.issues.filter((i) => i.status === "closed");
  const overdue = (t) => t.due_date && t.due_date < today && t.status !== "completed";
  const shownTasks = showDone ? data.tasks : openTasks;
  const shownIssues = showDone ? data.issues : openIssues;

  return (
    <div className="space-y-8 fade-in" data-testid="my-work-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Across all projects</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">My Work</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {openTasks.length} open task{openTasks.length !== 1 && "s"} · {openIssues.length} open issue{openIssues.length !== 1 && "s"}
            {openTasks.filter(overdue).length > 0 && (
              <span className="text-rose-500"> · {openTasks.filter(overdue).length} overdue</span>
            )}
          </p>
        </div>
        <Button variant="outline" size="sm" data-testid="toggle-completed-btn" onClick={() => setShowDone(!showDone)}>
          {showDone ? "Hide completed / closed" : `Show completed (${doneTasks.length + closedIssues.length})`}
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <div className="space-y-3">
          <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
            <CheckSquare className="h-4 w-4 text-emerald-500" /> Tasks ({shownTasks.length})
          </h2>
          <Card className="divide-y divide-border">
            {shownTasks.map((t) => (
              <button key={t.id} data-testid="my-work-task-item"
                onClick={() => navigate(`/projects/${t.project_id}?task=${t.id}`)}
                className="w-full text-left p-4 hover:bg-accent/40 transition-colors block">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-[10px] text-primary">{t.tid}</span>
                  <span className="text-[10px] font-mono uppercase text-muted-foreground border border-border rounded px-1.5 py-0.5">{t.project_key}</span>
                  <PriorityBadge priority={t.priority} />
                  <StatusBadge status={t.status} />
                  {overdue(t) && <span className="text-[10px] font-mono uppercase text-rose-500 border border-rose-500/40 rounded px-1.5 py-0.5" data-testid="overdue-badge">Overdue</span>}
                </div>
                <p className="text-sm font-medium mt-1.5">{t.title}</p>
                <div className="flex items-center gap-3 mt-2">
                  <Progress value={t.progress} className="h-1.5 w-24" />
                  <span className="text-[10px] font-mono text-muted-foreground">{t.progress}%</span>
                  <span className={`text-[10px] font-mono ml-auto ${overdue(t) ? "text-rose-500" : "text-muted-foreground"}`}>
                    due {t.due_date || "—"}
                  </span>
                </div>
              </button>
            ))}
            {!shownTasks.length && <p className="p-6 text-sm text-muted-foreground">No {showDone ? "" : "open "}tasks assigned to you. Enjoy the calm.</p>}
          </Card>
        </div>

        <div className="space-y-3">
          <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-amber-500" /> Issues ({shownIssues.length})
          </h2>
          <Card className="divide-y divide-border">
            {shownIssues.map((i) => (
              <button key={i.id} data-testid="my-work-issue-item"
                onClick={() => navigate(`/projects/${i.project_id}?tab=issues`)}
                className="w-full text-left p-4 hover:bg-accent/40 transition-colors flex items-start gap-3">
                {i.status === "open"
                  ? <CircleDot className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                  : <CheckCircle2 className="h-4 w-4 text-purple-500 mt-0.5 shrink-0" />}
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[10px] text-primary">{i.project_key} #{i.number}</span>
                    <PriorityBadge priority={i.priority} />
                    {(i.labels || []).map((l) => <LabelBadge key={l} label={l} />)}
                  </div>
                  <p className="text-sm font-medium mt-1">{i.title}</p>
                  <p className="text-[10px] font-mono text-muted-foreground mt-1">
                    opened by {i.created_by_name} · {new Date(i.created_at).toLocaleDateString()}
                  </p>
                </div>
              </button>
            ))}
            {!shownIssues.length && <p className="p-6 text-sm text-muted-foreground">No {showDone ? "" : "open "}issues assigned to you.</p>}
          </Card>
        </div>
      </div>
    </div>
  );
}
